import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Phone, Mail, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { BRAND, GITES } from "@/data";
import { EN, BOOK_TOGETHER } from "@/en";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.06, ease: "easeOut" } }),
};

export default function HouseEn({ id }) {
  const g = GITES.find((x) => x.id === id);
  const c = EN.cottages[id];
  const t = EN.house;
  const [active, setActive] = useState(null);

  return (
    <div data-testid={`page-house-${g.id}-en`} className="pb-24">
      <section className="relative h-[60vh] min-h-[420px]">
        <img src={g.cover} alt={g.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/70 to-transparent" />
        <div className="absolute bottom-0 inset-x-0">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
            <span className="text-cream/80 text-xs uppercase tracking-[0.28em]">{t.overline}</span>
            <h1 className="font-serif text-5xl lg:text-6xl font-light text-cream mt-2">{g.name}</h1>
            <a href={g.booking} target="_blank" rel="noopener noreferrer" data-testid={`house-reviews-${g.id}-en`} className="inline-flex items-center gap-2 mt-3 text-cream/90 text-sm underline underline-offset-4 hover:text-cream">
              {t.reviews}
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-12 mt-14">
        <div className="lg:col-span-2">
          <p className="font-serif text-2xl lg:text-3xl text-forest leading-snug">{c.tagline}</p>
          <p className="text-charcoal/70 leading-relaxed mt-6 text-lg">{c.description}</p>

          <h2 className="font-serif text-2xl text-forest mt-12 mb-5">{t.features}</h2>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
            {c.features.map((f) => (
              <div key={f} className="flex items-start gap-3 text-charcoal/75">
                <Check className="w-5 h-5 text-forest shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>{f}</span>
              </div>
            ))}
          </div>

          <div data-testid={`house-booktogether-${g.id}-en`} className="mt-10 rounded-2xl bg-stone/50 border border-sand p-6">
            <p className="text-charcoal/80 leading-relaxed">{BOOK_TOGETHER}</p>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-sand p-7 shadow-sm sticky top-28">
            <h3 className="font-serif text-2xl text-forest">{t.bookTitle}</h3>
            <p className="text-sm text-charcoal/60 mt-2">{t.bookText}</p>
            <div className="mt-6 flex flex-col gap-3">
              <a href={BRAND.emailLink} data-testid={`house-email-${g.id}-en`}>
                <Button className="w-full rounded-full bg-forest hover:bg-forest-dark text-cream h-11"><Mail className="mr-2 w-4 h-4" /> {t.email}</Button>
              </a>
              <a href={BRAND.phoneLink} data-testid={`house-phone-${g.id}-en`}>
                <Button variant="outline" className="w-full rounded-full border-forest/30 text-forest hover:bg-forest hover:text-cream h-11"><Phone className="mr-2 w-4 h-4" /> {t.call}</Button>
              </a>
              <a href={g.booking} target="_blank" rel="noopener noreferrer" data-testid={`house-booking-${g.id}-en`}>
                <Button variant="outline" className="w-full rounded-full border-brown/30 text-brown hover:bg-brown hover:text-cream h-11">{t.booking} <ExternalLink className="ml-2 w-4 h-4" /></Button>
              </a>
            </div>
            <div className="mt-6 pt-6 border-t border-sand text-sm text-charcoal/70 space-y-1">
              <p>{BRAND.phone}</p>
              <p>{BRAND.email}</p>
            </div>
          </div>
        </aside>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-20">
        <h2 className="font-serif text-3xl text-forest mb-8">{t.gallery}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {g.gallery.map((img, i) => (
            <motion.button key={i} data-testid={`house-gallery-${g.id}-en-${i}`} onClick={() => setActive({ src: img.src, alt: c.galleryAlt[i] || g.name })}
              initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i % 4}
              className={`overflow-hidden rounded-xl group ${i % 5 === 0 ? "col-span-2 row-span-2" : ""}`}>
              <img src={img.src} alt={c.galleryAlt[i] || g.name} loading="lazy" className="w-full h-full object-cover aspect-square group-hover:scale-105 transition-transform duration-700" />
            </motion.button>
          ))}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="house-lightbox-en" className="max-w-4xl p-0 overflow-hidden bg-forest-dark border-none">
          {active && (
            <div>
              <img src={active.src} alt={active.alt} className="w-full max-h-[80vh] object-contain" />
              <p className="text-cream/80 text-sm text-center py-3">{active.alt}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
